// Shared inline style for the dark slate-900 "primary" pill buttons used
// across the /live viewer (JoinCard's Sign in / Join as guest, ChatPanel's
// Send). antd's own Button component IS supposed to
// give a type="primary" button white label text via its own compiled
// `.ant-btn-primary` class — but that class has the SAME selector specificity
// as the base `.ant-btn` class (both single class selectors), so which one
// wins is a SOURCE-ORDER tie, not a specificity call. Worse: every one of
// these buttons also carries an INLINE `background`/`borderColor` override
// (needed because antd's default primary color is blue, not our slate-900
// brand pill) — inline styles beat every class rule regardless of order, so
// the background reliably stays dark even when antd's disabled-state class
// (`color: colorTextDisabled`, a dark gray) wins the color tie-break. A Send
// button is DISABLED the instant its own click empties the input (see
// ChatPanel.handleSend), so this is not a rare edge case — it happens on
// every single send. Root cause + fix: stop relying on the antd class-order
// coin flip for `color` at all — set it inline, explicitly, in both states,
// exactly like `background`/`borderColor` already are.
import type { CSSProperties } from "react";

const ENABLED: CSSProperties = {
  background: "#0f172a", // slate-900
  borderColor: "#0f172a",
  color: "#ffffff",
};

// Lighter than the enabled navy (slate-500, not slate-900) so a disabled
// pill still visibly reads as "a button, just not clickable right now" —
// rather than the near-invisible dark-gray-on-dark-navy the antd/inline
// collision above produced in production.
const DISABLED: CSSProperties = {
  background: "#64748b", // slate-500
  borderColor: "#64748b",
  color: "rgba(255,255,255,0.7)",
};

export function darkButtonStyle(disabled: boolean): CSSProperties {
  return disabled ? DISABLED : ENABLED;
}

// THE SHEET RUNS TWO CONTROL HEIGHTS, not five (Sep 2026 design critique, SP8):
// 52 for a primary action and for the choice tiles beside it, 44 for a
// secondary pill or an icon disc (those reach 44 through Tailwind classes). Rectangles take radius 12; only pills and
// discs are fully round. The geometry lives here rather than in Tailwind
// classes because the buttons that need it are antd Buttons and inline-styled
// natives, where a class loses the same specificity coin-flip the header above
// describes.
//
// It is deliberately NOT folded into darkButtonStyle itself: ChatPanel's Send
// is a 30px disc carrying the same colours, so colours and geometry have to
// stay separable.
const PRIMARY_HEIGHT = 52;
const RECTANGLE_RADIUS = 12;

/** The one full-width action on a sheet: Join the stream, Join to chat. */
export function darkPrimaryButtonStyle(disabled: boolean): CSSProperties {
  return {
    ...darkButtonStyle(disabled),
    height: PRIMARY_HEIGHT,
    borderRadius: RECTANGLE_RADIUS,
  };
}
